import { ErrorMessage } from '@components/core-ui/ErrorMessage';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { Select } from '@components/core-ui/form-elements/Select';
import EmailIcon from '@components/icons/Email.svg';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { EErrorMessages } from 'app/types/errorMessages';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AdminService } from 'app/API';
import { useNotifyQueryError } from 'app/hooks/useNotifyQueryError';
import { adminOrganizationsKeys } from 'app/queries/adminOrganizations/adminOrganizationsKeys';
import React, { useState } from 'react';
import toast from 'react-hot-toast';

import { createFetchAdminOrganizationTeamsQueryFn } from 'app/queries/adminOrganizations/createFetchAdminOrganizationTeamsQueryFn';
import { Content } from './InviteUserModal/styles/Content';

type TProps = {
  onCancel: () => void;
  onSuccess: () => void;
  organizationId: string;
};

export const AdminInviteUserModal = ({ onCancel, onSuccess, organizationId }: TProps) => {
  const { call } = useAPI();
  const [email, setEmail] = useState('');
  const [teamIds, setTeamIds] = useState<string[]>(['']);

  const teamsQuery = useQuery({
    queryKey: adminOrganizationsKeys.teams(organizationId),
    queryFn: createFetchAdminOrganizationTeamsQueryFn(call),
  });
  useNotifyQueryError(teamsQuery.error);

  const orgTeams = teamsQuery.data;

  const oneMoreTeam = async () => {
    const newTeamIds = [...teamIds, ''];
    setTeamIds(newTeamIds);
  };

  const teams = (orgTeams || [])
    .map((role) => ({ value: role.id, label: role.name }))
    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

  const changeTeamIds = async (index: number, value: string | undefined) => {
    const newTeamIds = [...teamIds];
    if (value) {
      newTeamIds[index] = value;
    } else {
      newTeamIds.splice(index, 1);
    }
    setTeamIds(newTeamIds);
  };

  const inviteMutation = useMutation({
    mutationFn: () => {
      const teams = teamIds.filter((teamId: string) => teamId && teamId !== '');
      return call(() =>
        AdminService.createWsInvitation({
          oid: organizationId,
          requestBody: { email: email.trim().toLowerCase(), teamIds: teams ?? [] },
        }),
      );
    },
    onSuccess: () => onSuccess(),
    onError: () => {
      toast.error(EErrorMessages.DEFAULT);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    inviteMutation.mutate();
  };

  return (
    <ModalWrapper width="300px">
      {teamsQuery.isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Invite Member</ModalTitle>
      <Content>
        <form action="#" onSubmit={handleSubmit}>
          <div className="inputWrapper">
            <Input
              icon={EmailIcon}
              required
              value={email}
              type="email"
              label="Email"
              name="email"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              error={inviteMutation.isPending && !email}
            />
            <Select
              value={teamIds[0]}
              options={teams}
              onChange={(_, value) => changeTeamIds(0, value)}
              label="Role"
              required
              name="role"
            />

            {teamIds.map((_teamId, index) => {
              if (index === 0) {
                return;
              }
              const options = (orgTeams ?? []).filter((team) => {
                return !teamIds.includes(team.id) || teamIds[index] === team.id;
              });
              return (
                <Select
                  key={`team-${index}`}
                  value={teamIds[index]}
                  options={options.map((team) => ({ value: team.id, label: team.name })) ?? []}
                  onChange={(_, value) => changeTeamIds(index, value)}
                  label="Role"
                  required
                  name="role"
                />
              );
            })}

            {teamsQuery.isError ? (
              <ErrorMessage error="Failed to load the organization roles. Please try again." />
            ) : null}

            {teamIds.length < 5 ? (
              <OutlinedButton type="button" style={{ width: 'auto' }} onClick={oneMoreTeam}>
                + Add more
              </OutlinedButton>
            ) : null}
          </div>
          <div className="buttonWrapper">
            <OutlinedButton className="w-full" onClick={onCancel}>
              Cancel
            </OutlinedButton>
            <Button
              className="w-full"
              type="submit"
              disabled={inviteMutation.isPending || !teamIds.length || !teamIds[0].length || !email}
            >
              Invite Member
            </Button>
          </div>
        </form>
      </Content>
    </ModalWrapper>
  );
};
